import React, {useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    Container,
    Divider,
    FormControl,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import {Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams} from 'react-router';
import {canAccessCollectionTab, sanitizeReturnTo} from '../collection/collectionTaskPayload';
import dayjs from 'dayjs';
import {EEI_PRIORITIES, EEI_STATUSES, getEei, updateEei} from '../../firebase/firestore/eei';
import {getUserDoc} from '../../firebase/firestore/users';
import {useSession} from '../../firebase/UserProvider';
import AuthorChip from '../products/components/common/AuthorChip';
import {toAvatarProfile, userDisplayName} from '../collection/components/PersonCell';
import EntityTagsSection from './tags/EntityTagsSection';
import EntityTargetsInput from './components/EntityTargetsInput';
import RequirementTargetsSection from './components/RequirementTargetsSection';
import {useEeiSimilarity} from './similarity/useEeiSimilarity';
import EeiSimilaritySection from './similarity/EeiSimilaritySection';
import RequirementCollectionPanel from './components/RequirementCollectionPanel';
import EeiFintelTab from './components/EeiFintelTab';
import IcrPriorityLabel from './components/IcrPriorityLabel';
import {useRequirementCollectionWork} from './useRequirementCollectionWork';

const AUTHORIZED_EDIT_ROLES = ['admin', 'collection_manager', 'collector'];
const ANALYTICS_TAB = 'analytics';
const OVERVIEW_TAB = 'overview';

/** Legacy ?tab= values from the brief Data / FINTEL rename. */
const LEGACY_TAB_ALIASES = {
    data: 'collection',
    fintel: 'analytics',
};

function normalizeTabParam(tab) {
    if (!tab) return null;
    return LEGACY_TAB_ALIASES[tab] || tab;
}

function sessionAsAvatarProfile(user) {
    if (!user) return null;
    return {
        displayName: user.displayName || user.name || user.email || null,
        hasAvatar: user.hasAvatar === true,
        profilePicture: user.profilePicture || user.photoURL || undefined,
    };
}

function resolvePrimaryIcr(eei) {
    const icrs = eei?.icrs || eei?.pirs || [];
    if (icrs.length > 0) return icrs[0];
    if (eei?.icrId) return {id: eei.icrId};
    return null;
}

function resolveIcrPath(icr) {
    if (!icr) return '/icr';
    const num = icr.icrNumber || icr.id;
    return num ? `/icr/${encodeURIComponent(num)}` : '/icr';
}

export default function EeiPage() {
    const {eeiNumber} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const {user, role, roles} = useSession();
    const canViewCollectionTab = canAccessCollectionTab(role, roles);
    const canManageTags = role === 'collection_manager' || role === 'admin'
        || (roles || []).some((r) => r === 'collection_manager' || r === 'admin');
    const canViewSimilarity = canManageTags;
    const canCreateTask = AUTHORIZED_EDIT_ROLES.includes(role)
        || (roles || []).some((r) => AUTHORIZED_EDIT_ROLES.includes(r));
    const canEditTargets = canCreateTask;

    const validTabs = useMemo(() => {
        const tabs = [OVERVIEW_TAB];
        if (canViewCollectionTab) tabs.push('collection');
        tabs.push(ANALYTICS_TAB);
        if (canViewSimilarity) tabs.push('similarity');
        return tabs;
    }, [canViewCollectionTab, canViewSimilarity]);
    const defaultTab = OVERVIEW_TAB;
    const urlTab = normalizeTabParam(searchParams.get('tab'));
    const [activeTab, setActiveTab] = React.useState(
        () => (validTabs.includes(urlTab) ? urlTab : defaultTab),
    );

    // Keep the active tab in sync with ?tab= so C2 returnTo (and browser
    // back/forward) restores the exact prior state.
    React.useEffect(() => {
        if (urlTab && validTabs.includes(urlTab) && urlTab !== activeTab) {
            setActiveTab(urlTab);
        } else if (urlTab && !validTabs.includes(urlTab) && activeTab !== defaultTab) {
            setActiveTab(defaultTab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlTab, validTabs]);

    const changeTab = (value) => {
        setActiveTab(value);
        const next = new URLSearchParams(searchParams);
        next.set('tab', value);
        setSearchParams(next, {replace: true});
    };

    const [eei, setEei] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [editing, setEditing] = React.useState(false);
    const [form, setForm] = React.useState({});
    const [error, setError] = React.useState(null);
    const [saving, setSaving] = React.useState(false);

    const primaryIcr = useMemo(() => resolvePrimaryIcr(eei), [eei]);
    const icrPath = resolveIcrPath(primaryIcr);

    const similarity = useEeiSimilarity(eei?.id, canViewSimilarity && Boolean(eei?.id));
    const collectionWork = useRequirementCollectionWork('eei', eei?.id);
    const coveragePct = collectionWork.data?.coveragePct ?? null;
    const coveredSummary = collectionWork.data?.coveredSummary || null;
    const uncoveredSummary = collectionWork.data?.uncoveredSummary || null;
    const gapText = collectionWork.data?.collectionGapText || null;
    const ctPrefill = collectionWork.data?.ctPrefill || null;
    // Gap narratives mean collection is still needed; no coverage run at all
    // should still offer Create CT (fully covered = coverage data, no gap).
    const needsCollection = Boolean(uncoveredSummary || gapText);
    const hasCoverageRun = coveragePct != null
        || Boolean(String(coveredSummary || '').trim())
        || Boolean(String(uncoveredSummary || '').trim())
        || Boolean(String(gapText || '').trim());
    const allowCreateTask = needsCollection || !hasCoverageRun;

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getEei(eeiNumber);
            if (!data) {
                setError('EEI not found');
                setEei(null);
            } else {
                setEei(data);
                setForm({
                    text: data.text || '',
                    status: data.status || 'Draft',
                    priority: data.priority || 'Medium',
                    owner: data.owner || '',
                    keywords: (data.keywords || []).join(', '),
                    targets: Array.isArray(data.targets) ? data.targets : [],
                });
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eeiNumber]);

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            const payload = {
                text: form.text.trim(),
                status: form.status,
                owner: form.owner.trim() || null,
                keywords: form.keywords.split(',').map((k) => k.trim()).filter(Boolean),
                targets: Array.isArray(form.targets) ? form.targets : [],
            };
            if (canManageTags) {
                payload.priority = form.priority;
            }
            const updated = await updateEei(eei.id || eeiNumber, payload);
            setEei(updated);
            setEditing(false);
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const authorUserId = eei?.createdBy || eei?.owner || null;
    const [authorProfile, setAuthorProfile] = useState(null);

    // AuthorChip only fetches the expert profile when the dialog opens, so the
    // inline label needs a resolved display name (same pattern as IcrPage).
    useEffect(() => {
        if (!authorUserId) {
            setAuthorProfile(null);
            return undefined;
        }
        if (authorUserId === user?.uid || authorUserId === user?.id) {
            setAuthorProfile(sessionAsAvatarProfile(user));
            return undefined;
        }
        let cancelled = false;
        getUserDoc(authorUserId)
            .then((doc) => {
                if (cancelled || !doc) return;
                setAuthorProfile(toAvatarProfile(doc));
            })
            .catch(() => {
                if (!cancelled) setAuthorProfile(null);
            });
        return () => { cancelled = true; };
    }, [authorUserId, user]);

    const authorDisplayName = userDisplayName(authorProfile)
        || (eei?.owner && eei.owner !== authorUserId ? eei.owner : null)
        || authorUserId;

    const resolveCreateTaskPrefill = () => {
        // When coverage identified gaps, keep the richer server prefill.
        if (needsCollection && ctPrefill) return ctPrefill;
        // No coverage run: title + description are just the EEI question.
        const question = String(eei?.text || '').trim();
        return {
            eeiId: eei.id,
            eeiNumber: eei.eeiNumber || null,
            suggestedTitle: question,
            description: question,
            priority: ctPrefill?.priority || eei.priority || 'Medium',
            classification: ctPrefill?.classification || 'UNCLASSIFIED',
        };
    };

    const handleCreateTask = canCreateTask && eei?.id
        ? () => navigate(
            `/collection/tasks/create?eeiId=${encodeURIComponent(eei.id)}`,
            {state: {eeis: [eei], prefill: resolveCreateTaskPrefill()}},
        )
        : undefined;

    const formatCreatedDate = (value) => (
        value && dayjs(value).isValid() ? dayjs(value).format('MMM D, YYYY HH:mm') : null
    );

    const handleBack = () => {
        const returnTo = sanitizeReturnTo(location.state?.returnTo);
        if (returnTo) {
            navigate(returnTo);
            return;
        }
        navigate(-1);
    };

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{py: {xs: 2, md: 3}}}>
                <Typography>Loading…</Typography>
            </Container>
        );
    }

    if (!eei) {
        return (
            <Container maxWidth="xl" sx={{py: {xs: 2, md: 3}}}>
                <Stack spacing={2}>
                    <Typography color="error">{error || 'Not found'}</Typography>
                    <Button variant="outlined" size="small" onClick={handleBack}>
                        Back
                    </Button>
                </Stack>
            </Container>
        );
    }

    const icrNumber = primaryIcr?.icrNumber || null;
    const icrTitle = primaryIcr?.title || primaryIcr?.question || null;
    const createdAtLabel = formatCreatedDate(eei.createdAt);

    return (
        <Container maxWidth="xl" sx={{py: {xs: 2, md: 3}}}>
            <Paper elevation={0} sx={{
                p: {xs: 2, sm: 3.5},
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
            }}>
                <Stack spacing={3}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleBack}
                        >
                            Back
                        </Button>
                        <Stack direction="row" spacing={1}>
                            {!editing && (
                                <Button size="small" variant="outlined" onClick={() => setEditing(true)}>
                                    Edit
                                </Button>
                            )}
                        </Stack>
                    </Stack>

                    {editing ? (
                        <Stack spacing={2}>
                            <TextField
                                label="EEI text"
                                multiline
                                minRows={3}
                                fullWidth
                                value={form.text}
                                onChange={(e) => setForm((p) => ({...p, text: e.target.value}))}
                            />
                            <FormControl size="small" fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    label="Status"
                                    value={form.status}
                                    onChange={(e) => setForm((p) => ({...p, status: e.target.value}))}
                                >
                                    {EEI_STATUSES.map((s) => (
                                        <MenuItem key={s} value={s}>{s}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {canManageTags && (
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Priority</InputLabel>
                                    <Select
                                        label="Priority"
                                        value={form.priority}
                                        onChange={(e) => setForm((p) => ({...p, priority: e.target.value}))}
                                    >
                                        {EEI_PRIORITIES.map((p) => (
                                            <MenuItem key={p} value={p}>{p}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            <TextField
                                size="small"
                                label="Owner"
                                fullWidth
                                value={form.owner}
                                onChange={(e) => setForm((p) => ({...p, owner: e.target.value}))}
                            />
                            <TextField
                                size="small"
                                label="Keywords (comma-separated)"
                                fullWidth
                                value={form.keywords}
                                onChange={(e) => setForm((p) => ({...p, keywords: e.target.value}))}
                            />
                            <EntityTargetsInput
                                value={form.targets}
                                onChange={(targets) => setForm((p) => ({...p, targets}))}
                            />
                            {error && <Typography color="error" variant="body2">{error}</Typography>}
                            <Stack direction="row" spacing={1}>
                                <Button variant="contained" onClick={save} disabled={saving}>
                                    {saving ? 'Saving…' : 'Save'}
                                </Button>
                                <Button onClick={() => setEditing(false)}>Cancel</Button>
                            </Stack>
                        </Stack>
                    ) : (
                        <>
                            <Stack
                                direction={{xs: 'column', sm: 'row'}}
                                justifyContent="space-between"
                                alignItems={{xs: 'flex-start', sm: 'center'}}
                                spacing={1.5}
                            >
                                <Stack spacing={0.5} sx={{minWidth: 0, flex: 1}}>
                                    <Typography variant="h4" fontWeight={700} sx={{lineHeight: 1.2}}>
                                        {eei.text || 'EEI'}
                                    </Typography>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={0.75}
                                        flexWrap="wrap"
                                        useFlexGap
                                        sx={{color: 'text.secondary'}}
                                    >
                                        <Typography variant="body2" color="inherit" component="span" fontFamily="monospace">
                                            {eei.eeiNumber}
                                        </Typography>
                                        {(authorUserId || eei.owner) && (
                                            <>
                                                <Typography variant="body2" color="inherit" component="span">•</Typography>
                                                {authorUserId ? (
                                                    <Box
                                                        sx={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            minWidth: 0,
                                                            '& .MuiAvatar-root': {
                                                                width: 20,
                                                                height: 20,
                                                                fontSize: '0.65rem',
                                                            },
                                                            '& .MuiLink-root': {
                                                                fontSize: '0.875rem',
                                                                color: 'text.secondary',
                                                                textDecoration: 'none',
                                                            },
                                                        }}
                                                    >
                                                        <AuthorChip
                                                            userId={authorUserId}
                                                            userDisplayName={authorDisplayName}
                                                            flex={undefined}
                                                            avatarProfile={authorProfile}
                                                        />
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="inherit" component="span">
                                                        {eei.owner}
                                                    </Typography>
                                                )}
                                            </>
                                        )}
                                        {createdAtLabel && (
                                            <>
                                                <Typography variant="body2" color="inherit" component="span">•</Typography>
                                                <Typography variant="body2" color="inherit" component="span">
                                                    {createdAtLabel}
                                                </Typography>
                                            </>
                                        )}
                                    </Stack>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Chip
                                        label={eei.status}
                                        size="small"
                                        color={eei.status === 'Active' ? 'success' : 'default'}
                                    />
                                    <IcrPriorityLabel priority={eei.priority || 'Medium'}/>
                                </Stack>
                            </Stack>

                            {primaryIcr && (
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Typography variant="body2" color="text.secondary">
                                        Parent ICR
                                    </Typography>
                                    <Link
                                        component={RouterLink}
                                        to={icrPath}
                                        variant="body2"
                                        fontWeight={600}
                                        underline="hover"
                                    >
                                        {icrNumber || primaryIcr.id}
                                        {icrTitle ? ` — ${icrTitle}` : ''}
                                    </Link>
                                </Stack>
                            )}

                            <Tabs
                                value={activeTab}
                                onChange={(_, v) => changeTab(v)}
                                sx={{minHeight: 36, borderBottom: 1, borderColor: 'divider'}}
                            >
                                <Tab
                                    value="overview"
                                    label="Overview"
                                    sx={{minHeight: 36, textTransform: 'none', fontWeight: 600}}
                                />
                                {canViewCollectionTab && (
                                    <Tab
                                        value="collection"
                                        label="Collection"
                                        sx={{minHeight: 36, textTransform: 'none', fontWeight: 600}}
                                    />
                                )}
                                <Tab
                                    value="analytics"
                                    label="Analytics"
                                    sx={{minHeight: 36, textTransform: 'none', fontWeight: 600}}
                                />
                                {canViewSimilarity && (
                                    <Tab
                                        value="similarity"
                                        label="Similarity"
                                        sx={{minHeight: 36, textTransform: 'none', fontWeight: 600}}
                                    />
                                )}
                            </Tabs>

                            {activeTab === 'overview' && (
                                <Stack spacing={3}>
                                    <Stack spacing={1.25}>
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            Question
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{lineHeight: 1.75, maxWidth: '84ch'}}
                                        >
                                            {eei.text || '—'}
                                        </Typography>
                                    </Stack>
                                    <RequirementTargetsSection
                                        value={eei.targets}
                                        canEdit={canEditTargets && Boolean(eei.id)}
                                        onSave={async (refs) => {
                                            const updated = await updateEei(eei.id || eeiNumber, {
                                                targets: refs,
                                            });
                                            setEei(updated || {
                                                ...eei,
                                                targets: refs,
                                                targetsLocked: true,
                                            });
                                        }}
                                    />
                                </Stack>
                            )}

                            {activeTab === 'collection' && (
                                <RequirementCollectionPanel
                                    tasks={collectionWork.tasks}
                                    loading={collectionWork.loading}
                                    error={collectionWork.error}
                                    coveragePct={coveragePct}
                                    coveredSummary={coveredSummary}
                                    uncoveredSummary={uncoveredSummary}
                                    gapText={gapText}
                                    canCreateTask={canCreateTask}
                                    onCreateTask={allowCreateTask ? handleCreateTask : undefined}
                                />
                            )}

                            {activeTab === 'analytics' && (
                                <EeiFintelTab eeiId={eei.id}/>
                            )}

                            {canViewSimilarity && activeTab === 'similarity' && (
                                <EeiSimilaritySection similarity={similarity}/>
                            )}

                            {canManageTags && eei.id && (
                                <>
                                    <Divider/>
                                    <EntityTagsSection
                                        entityType="EEI"
                                        entityId={eei.id}
                                        canEdit={canManageTags}
                                        enabled={canManageTags}
                                    />
                                </>
                            )}
                        </>
                    )}
                </Stack>
            </Paper>
        </Container>
    );
}
